import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Loader from "@components/Loader";
import {
  useCouponsQuery,
  useCreateCouponMutation,
  useSaveCouponMutation,
  useDeleteCouponMutation
} from "@queries/coupons";
import "@styles/opencart-admin.css";

const todayISO = () => new Date().toISOString().slice(0, 10);
const oneMonthLaterISO = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
};

const defaultForm = {
  name: "",
  code: "",
  type: "P",
  discount: 0,
  total: 0,
  logged: false,
  shipping: false,
  uses_total: 0,
  uses_customer: 0,
  status: true,
  date_start: todayISO(),
  date_end: oneMonthLaterISO(),
  option: ""
};

export default function CouponsPage() {
  const [filters, setFilters] = React.useState({
    name: "",
    code: ""
  });
  const [page, setPage] = React.useState(1);
  const pageSize = 20;

  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(defaultForm);
  const [validationErrors, setValidationErrors] = React.useState({});
  const [highlightForm, setHighlightForm] = React.useState(false);
  const formCardRef = React.useRef(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, isFetching, isError, error } = useCouponsQuery(
    {
      name: filters.name || undefined,
      code: filters.code || undefined
    },
    page,
    pageSize
  );

  const createMutation = useCreateCouponMutation();
  const saveMutation = useSaveCouponMutation();
  const deleteMutation = useDeleteCouponMutation();

  const rows = data?.data || [];
  const total = data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  React.useEffect(() => {
    if (!highlightForm) return undefined;

    const timeoutId = window.setTimeout(() => {
      setHighlightForm(false);
    }, 1800);

    return () => window.clearTimeout(timeoutId);
  }, [highlightForm]);

  function resetForm() {
    setEditingId(null);
    setForm(defaultForm);
    setValidationErrors({});
    setHighlightForm(false);
  }

  function openEdit(row) {
    setValidationErrors({});
    setEditingId(row.coupon_id);
    setForm({
      name: row.name || "",
      code: row.code || "",
      type: row.type || "P",
      discount: row.discount ?? 0,
      total: row.total ?? 0,
      logged: row.logged ? true : false,
      shipping: row.shipping ? true : false,
      uses_total: row.uses_total ?? 0,
      uses_customer: row.uses_customer ?? 0,
      status: row.status ? true : false,
      date_start: row.date_start ? row.date_start.substring(0, 10) : "",
      date_end: row.date_end ? row.date_end.substring(0, 10) : "",
      option: row.option || ""
    });

    setHighlightForm(true);
    window.requestAnimationFrame(() => {
      formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  async function handleSave(e) {
    e.preventDefault();
    setValidationErrors({});

    const payload = {
      name: form.name,
      code: form.code,
      type: form.type,
      discount: Number(form.discount) || 0,
      total: Number(form.total) || 0,
      logged: form.logged ? 1 : 0,
      shipping: form.shipping ? 1 : 0,
      uses_total: Number(form.uses_total) || 0,
      uses_customer: Number(form.uses_customer) || 0,
      status: form.status ? 1 : 0,
      date_start: form.date_start || null,
      date_end: form.date_end || null,
      option: form.option
    };

    try {
      if (editingId) {
        await saveMutation.mutateAsync({ id: editingId, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }

      resetForm();
      queryClient.invalidateQueries({ queryKey: ["marketing", "coupons"] });
    } catch (err) {
      if (err?.response?.status === 422 && err?.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      } else {
        alert("Failed to save coupon");
      }
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this coupon?")) return;
    await deleteMutation.mutateAsync(id);
    queryClient.invalidateQueries({ queryKey: ["marketing", "coupons"] });
  }

  function handleFilterChange(field, value) {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  }

  function formatDate(d) {
    if (!d) return "";
    if (typeof d === "string") return d.substring(0, 10);
    try {
      return new Date(d).toISOString().substring(0, 10);
    } catch {
      return "";
    }
  }

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Coupons</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Coupons</span>
        </nav>
      </div>

      <div
        className="oc-card"
        ref={formCardRef}
        style={{
          scrollMarginTop: 24,
          border: highlightForm ? "2px solid #1d4ed8" : undefined,
          boxShadow: highlightForm
            ? "0 0 0 4px rgba(29, 78, 216, 0.16), 0 18px 40px rgba(29, 78, 216, 0.14)"
            : undefined,
          transition: "border-color 180ms ease, box-shadow 180ms ease"
        }}
      >
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>{editingId ? "Edit Coupon" : "Add Coupon"}</strong>
          </div>
          <div>
            <button className="oc-btn ghost" onClick={resetForm}>
              New
            </button>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label>Coupon Name</label>
              <input
                className="oc-input"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                required
              />
              {validationErrors.name && (
                <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                  {validationErrors.name[0]}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Code</label>
              <input
                className="oc-input"
                value={form.code}
                onChange={(e) =>
                  setForm((f) => ({ ...f, code: e.target.value }))
                }
                required
              />
              {validationErrors.code && (
                <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                  {validationErrors.code[0]}
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Type</label>
              <select
                className="oc-input"
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value }))
                }
              >
                <option value="P">Percentage</option>
                <option value="F">Fixed Amount</option>
              </select>
            </div>
            <div className="form-group">
              <label>Discount</label>
              <input
                type="number"
                className="oc-input"
                value={form.discount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, discount: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Total</label>
              <input
                type="number"
                className="oc-input"
                value={form.total}
                onChange={(e) =>
                  setForm((f) => ({ ...f, total: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Sort / Uses Total</label>
              <input
                type="number"
                className="oc-input"
                value={form.uses_total}
                onChange={(e) =>
                  setForm((f) => ({ ...f, uses_total: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Uses Per Customer</label>
              <input
                type="number"
                className="oc-input"
                value={form.uses_customer}
                onChange={(e) =>
                  setForm((f) => ({ ...f, uses_customer: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Option</label>
              <input
                className="oc-input"
                value={form.option}
                onChange={(e) =>
                  setForm((f) => ({ ...f, option: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date Start</label>
              <input
                type="date"
                className="oc-input"
                value={form.date_start}
                onChange={(e) =>
                  setForm((f) => ({ ...f, date_start: e.target.value }))
                }
              />
              {validationErrors.date_start && (
                <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                  {validationErrors.date_start[0]}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Date End</label>
              <input
                type="date"
                className="oc-input"
                value={form.date_end}
                onChange={(e) =>
                  setForm((f) => ({ ...f, date_end: e.target.value }))
                }
              />
              {validationErrors.date_end && (
                <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                  {validationErrors.date_end[0]}
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                <label>
                  <input
                    type="radio"
                    name="status"
                    checked={form.status === true}
                    onChange={() =>
                      setForm((f) => ({ ...f, status: true }))
                    }
                  />{" "}
                  Enabled
                </label>
                <label>
                  <input
                    type="radio"
                    name="status"
                    checked={form.status === false}
                    onChange={() =>
                      setForm((f) => ({ ...f, status: false }))
                    }
                  />{" "}
                  Disabled
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Options</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label>
                  <input
                    type="checkbox"
                    checked={form.logged}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        logged: e.target.checked
                      }))
                    }
                  />{" "}
                  Logged
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={form.shipping}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        shipping: e.target.checked
                      }))
                    }
                  />{" "}
                  Free Shipping
                </label>
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8
            }}
          >
            <button
              type="button"
              className="oc-btn ghost"
              onClick={resetForm}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="oc-btn primary"
              disabled={
                createMutation.isLoading || saveMutation.isLoading
              }
            >
              Save
            </button>
          </div>
        </form>
      </div>

      <div className="oc-card" style={{ marginTop: 16 }}>
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Coupon List</strong>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 260px",
            gap: 16
          }}
        >
          <div>
            {isError && (
              <div style={{ color: "#c00", marginBottom: 8 }}>
                Error: {error?.message || "Unknown error"}
              </div>
            )}

            <table className="oc-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th style={{ width: 90 }}>Type</th>
                  <th style={{ width: 90 }}>Discount</th>
                  <th style={{ width: 120 }}>Date Start</th>
                  <th style={{ width: 120 }}>Date End</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 200 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: 24 }}>
                      No coupons
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.coupon_id}>
                    <td>{row.name}</td>
                    <td>{row.code}</td>
                    <td>{row.type}</td>
                    <td>{row.discount != null && row.discount !== "" ? Number(row.discount).toFixed(2) : ""}</td>
                    <td>{formatDate(row.date_start)}</td>
                    <td>{formatDate(row.date_end)}</td>
                    <td>{row.status ? "Enabled" : "Disabled"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="oc-btn blue"
                          onClick={() => openEdit(row)}
                        >
                          edit
                        </button>
                        <button
                          className="oc-btn danger"
                          onClick={() => handleDelete(row.coupon_id)}
                        >
                          delete
                        </button>
                        <button
                          className="oc-btn ghost"
                          onClick={() =>
                            navigate(`/marketing/coupons/${row.coupon_id}/orders`)
                          }
                        >
                          orders
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="oc-card" style={{ margin: 0 }}>
            <div className="title">Filter</div>
            <div className="form-group">
              <label>Name</label>
              <input
                className="oc-input"
                value={filters.name}
                onChange={(e) =>
                  handleFilterChange("name", e.target.value)
                }
              />
            </div>
            <div className="form-group">
              <label>Code</label>
              <input
                className="oc-input"
                value={filters.code}
                onChange={(e) =>
                  handleFilterChange("code", e.target.value)
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className="oc-card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 16
        }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> (
          {total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            « First
          </button>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ‹ Prev
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next ›
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage(pages)}
          >
            Last »
          </button>
        </div>
      </div>
    </div>
  );
}

